import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from '~/utils/navigation';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { showNotification } from '~/components/notifications/ducks/notifications.js';
import usePrevious from '~/hooks/use-previous.js';
import useTranslations from '~/components/translation/use-translations.ts';
import useCasinoRoutes from '~/components/router/use-casino-routes';
import {
	realityCheckCoolOffTimerStart,
	realityCheckTimeReachedToggle,
	startGermany,
	toggleCoolOffPeriod
} from './ducks/index.js';
import {
	germanPopUpShown,
	getCoolOff,
	getCurrentDate,
	getPopupInfo,
	getRCAFromCookies,
	getTimestamp,
	isRCAccepted,
	postCoolOff,
	setGermanRCAcknowledgement,
	setRCACookie,
	setTimestamp
} from './reality-check-utils.js';
import {
	selectCoolOffPeriodOn,
	selectCoolOffTimerId,
	selectRealityCheckTimeReached,
	selectTimerId
} from './ducks/selectors.js';
import { currentGameSelector } from '~/components/casino-shared/game-loader/ducks/selectors';
import RealityCheckView from './reality-check-view.js';
import { APP_EVENTS, registerAppEventCallback, sendAppBridgeEvent, WEB_EVENTS } from '~/components/app-bridge/index.js';
import BinaryQuestion from '~/components/binary-question';
import reloadPage from '~/components/account/reality-check/utils';

const textKeys = [
	'MOBILE_REALITY_CHECK_HEADER',
	'MOBILE_REALITY_CHECK_TIME_PLAYED_HEADER',
	'MOBILE_REALITY_CHECK_BET_SUM_HEADER',
	'MOBILE_REALITY_CHECK_PLAY_TIME',
	'MOBILE_REALITY_CHECK_TIME_COOLDOWN',
	'MOBILE_REALITY_CHECK_BET_SUM',
	'MOBILE_REALITY_CHECK_MESSAGE_UPPER',
	'MOBILE_REALITY_CHECK_CONTINUE_BUTTON',
	'MOBILE_REALITY_CHECK_COOLOFF_REACHED_CAN_PLAY_AGAIN',
	'MOBILE_REALITY_CHECK_COOLDOWN_PERIOD',
	'API_ERROR_TITLE',
	'API_ERROR_DESCRIPTION',
	'BUTTON_OK'
];

/**
 * Reality Check Germany Logic
 * Due to cooloff timer logic its not combined with other reality check.
 * If requirement change can be integrated to Reality Check
 */
const RealityCheckGermany = () => {
	// states
	const [show, setShow] = useState(false);
	const [betSum, setBetSum] = useState();
	const [playedTime, setPlayedTime] = useState();
	const [apiError, setApiError] = useState(false);

	// Selectors
	const authenticated = useSelector(authenticationSelector);
	const prevAuthenticated = usePrevious(authenticated);
	const timerId = useSelector(selectTimerId);
	const realityCheckTimeReached = useSelector(selectRealityCheckTimeReached);
	const coolOffTimerId = useSelector(selectCoolOffTimerId);
	const coolOffPeriodOn = useSelector(selectCoolOffPeriodOn);
	const currentGame = useSelector(currentGameSelector);
	const { ROUTE_CASINO_EXPLORE } = useCasinoRoutes();

	// Other hooks
	const router = useRouter();
	const { translations } = useTranslations(textKeys);
	const dispatch = useDispatch();

	const startRealityCheck = useCallback(
		async timestamp => {
			if (timestamp === undefined || Number.isNaN(timestamp)) {
				try {
					const response = await setTimestamp();
					if (response.status === 'SUCCESS') {
						dispatch(startGermany(getTimestamp()));
					} else {
						// error response handling show popup with error message and reload the page upon confirmation
						setApiError(true);
					}
				} catch (e) {
					// TODO error log Sentry maybe?
					setApiError(true);
				}
			} else {
				dispatch(startGermany(timestamp));
			}
		},
		[dispatch]
	);

	const getCoolOffTime = useCallback(async () => {
		const response = await getCoolOff();
		const responseInMS = Number(response) * 1000;
		if (responseInMS) {
			dispatch(toggleCoolOffPeriod(true, responseInMS));
		} else {
			dispatch(toggleCoolOffPeriod(false, undefined));
		}
		sendAppBridgeEvent(WEB_EVENTS.LOG_EVENT, [
			{
				log: `getCoolOffTime:
				response: ${response},
				responseInMS: ${responseInMS}`
			}
		]);
		return responseInMS;
	}, [dispatch]);

	const startCoolOff = useCallback(async () => {
		const response = await postCoolOff();
		if (Number.isNaN(response)) {
			return null;
		}
		return response * 1000;
	}, []);

	const afterCoolOffFinished = useCallback(() => {
		dispatch(
			showNotification({
				messageKey: 'MOBILE_REALITY_CHECK_COOLOFF_REACHED_CAN_PLAY_AGAIN',
				color: 'neutral',
				autoHide: true
			})
		);
		dispatch(toggleCoolOffPeriod(false, undefined));
	}, [dispatch]);

	const setUpCoolOffTimer = useCallback(
		coolOffRemainingTime => {
			clearTimeout(coolOffTimerId);
			const newCoolOffTimerId = setTimeout(() => {
				afterCoolOffFinished();
				// reset RC timer when cooldown finishes, reset cookies?
				try {
					setTimestamp().then(() => {
						// start the next reality check
						startRealityCheck(getTimestamp());
					});
				} catch (err) {
					// TODO add Sentry error?
					// console.warn('reality-check-german.js: showRealityCheck: fetch failed', err);
					setApiError(true);
				}
			}, coolOffRemainingTime);
			dispatch(realityCheckCoolOffTimerStart(newCoolOffTimerId));
		},
		[afterCoolOffFinished, coolOffTimerId, dispatch, startRealityCheck]
	);

	const onContinue = () => {
		try {
			setGermanRCAcknowledgement(true).then(async response => {
				if (response.status === 'SUCCESS') {
					const { result } = response;
					const { coolOff } = result;
					const now = await getCurrentDate();
					const popupAcknowledgedTimestamp = result.RCAPCCC;
					let coolOffRemainingTime;
					let coolOffTimestamp;

					if (coolOff.on && !Number.isNaN(coolOff.timestamp)) {
						// start FE countdown with coolOff timestamp (in seconds) already started on BE
						coolOffTimestamp = coolOff.timestamp * 1000;
						coolOffRemainingTime = coolOffTimestamp - now;
						if (coolOffRemainingTime > 0) setUpCoolOffTimer(coolOffRemainingTime);
					} else {
						// start coolOff from FE
						await startCoolOff().then(async res => {
							// cooloff service returns a timestamp with the end of cooldown period
							if (!Number.isNaN(res)) {
								coolOffTimestamp = res * 1000;
								coolOffRemainingTime = coolOffTimestamp - now;
								setUpCoolOffTimer(coolOffRemainingTime);
							}
						});
					}
					dispatch(realityCheckTimeReachedToggle(false));
					dispatch(toggleCoolOffPeriod(true, coolOffTimestamp));
					sendAppBridgeEvent(WEB_EVENTS.LOG_EVENT, [
						{
							log: `onContinue startCoolOff.then:
												coolOffRemainingTime: ${coolOffRemainingTime},
												response: ${response},
												popupAcknowledgedTimestamp: ${popupAcknowledgedTimestamp}`
						}
					]);
					setShow(false);
					setRCACookie('accepted');
					if (currentGame && coolOffPeriodOn) {
						router.push(ROUTE_CASINO_EXPLORE);
					}
				} else {
					// TODO better error handling?
					setApiError(true);
				}
			});
		} catch (err) {
			// TODO send to Sentry?
			setApiError(true);
		}
	};

	const resetRealityCheckPostLogout = useCallback(() => {
		dispatch(realityCheckCoolOffTimerStart(undefined));
		dispatch(toggleCoolOffPeriod(false, undefined));
		setShow(false);
		clearTimeout(timerId);
		clearTimeout(coolOffTimerId);
	}, [coolOffTimerId, dispatch, timerId]);

	const showRCPopup = () => {
		germanPopUpShown().then(() => {
			setShow(true);
			setRCACookie('not-accepted');
			sendAppBridgeEvent(WEB_EVENTS.LOG_EVENT, [
				{
					log: `showRCPopup setAcknowledgementTimestamp.then`
				}
			]);
		});
	};

	const showRealityCheck = useCallback(async () => {
		if (authenticated) {
			// Get data for popup
			try {
				const response = await getPopupInfo();
				if (
					response.status === 'SUCCESS' &&
					response.result.betSum &&
					response.result.texts &&
					(response.result.playTimeMinutes > 0 || response.result.playTimeHours > 0)
				) {
					// Probably limit the info for this call on BE?
					setBetSum(response.result.betSum);
					setPlayedTime(
						`${response.result.playTimeHours} ${response.result.texts.hours}, ${response.result.playTimeMinutes} ${response.result.texts.minutes}`
					);
					showRCPopup();
				} else {
					setApiError(true);
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.warn('reality-check-german.js: showRealityCheck: fetch failed', err);
				setApiError(true);
			}
		}
	}, [authenticated]);

	const startOnLoad = useCallback(() => {
		let remainingTimerId;
		const start = async () => {
			const coolOffExpirationTimestamp = await getCoolOffTime();
			// response is the timestamp when cool off ends
			if (coolOffExpirationTimestamp) {
				// cool off period is ongoing so use coolOffExpirationTimestamp and start reality check after cool off expires
				const now = await getCurrentDate();
				const coolOffRemainingTime = coolOffExpirationTimestamp - now;
				if (coolOffRemainingTime > 0) setUpCoolOffTimer(coolOffRemainingTime);
				sendAppBridgeEvent(WEB_EVENTS.LOG_EVENT, [
					{
						log: `startOnLoad cooloff remainingTimerId:
						remainingTime: ${coolOffRemainingTime},
						timestamp: ${coolOffExpirationTimestamp}`
					}
				]);
				// Add check here to show the popup if the user hasn't clicked continue
			} else if (!isRCAccepted() && getRCAFromCookies() !== null) {
				// startRealityCheck();
				showRealityCheck();
			} else {
				startRealityCheck(getTimestamp());
			}
		};
		if (authenticated) {
			start();
		}
		return () => {
			clearTimeout(remainingTimerId);
		};
	}, [authenticated, getCoolOffTime, setUpCoolOffTimer, showRealityCheck, startRealityCheck]);

	// MOBILE APP RELATED EVENT
	useEffect(() => {
		registerAppEventCallback(APP_EVENTS.START_REALITY_CHECK, startOnLoad);
	}, [startOnLoad]);

	// effects
	// component did mount
	useEffect(() => {
		startOnLoad();
	}, []); // eslint-disable-line

	useEffect(() => {
		return () => clearTimeout(timerId);
	}, [timerId]);

	useEffect(() => {
		return () => clearTimeout(coolOffTimerId);
	}, [coolOffTimerId]);

	// component update
	useEffect(() => {
		if (typeof prevAuthenticated !== 'undefined' && prevAuthenticated !== authenticated) {
			if (authenticated) {
				startRealityCheck();
			} else {
				resetRealityCheckPostLogout();
			}
		}
	}, [authenticated, prevAuthenticated, resetRealityCheckPostLogout, startRealityCheck]);

	useEffect(() => {
		if (realityCheckTimeReached) {
			showRealityCheck();
		}
	}, [realityCheckTimeReached]); // eslint-disable-line

	/**
	 * If the wrapper native app is in the native state (eg: Showing a native view) it should be switched to
	 * the webview in order to be able to show reality check to users in time.
	 */
	useEffect(() => {
		sendAppBridgeEvent(WEB_EVENTS.REALITY_CHECK_POPUP, [{ show }]);
	}, [show]);

	const { MOBILE_REALITY_CHECK_CONTINUE_BUTTON } = translations;

	const popupAccepted = !isRCAccepted();

	if (!show || !popupAccepted) {
		return apiError ? (
			<BinaryQuestion
				shouldOpenBinary
				title={translations.API_ERROR_TITLE}
				text={translations.API_ERROR_DESCRIPTION}
				onYesClick={reloadPage}
				yesText={translations.BUTTON_OK}
				fixedPosition
			/>
		) : null;
	}

	const buttonConfig = [
		{
			onClick: onContinue,
			dataAt: 'continue-button-reality-check',
			text: MOBILE_REALITY_CHECK_CONTINUE_BUTTON
		}
	];

	// texts to be passed to view
	const texts = { ...translations, betSum, playedTime };

	return apiError ? (
		<BinaryQuestion
			shouldOpenBinary
			title={translations.API_ERROR_TITLE}
			text={translations.API_ERROR_DESCRIPTION}
			onYesClick={reloadPage}
			yesText={translations.BUTTON_OK}
			fixedPosition
		/>
	) : (
		<RealityCheckView texts={texts} buttonConfig={buttonConfig} isGermanFranchise />
	);
};

export default RealityCheckGermany;
